import { apiPost, apiGet, setTokens, clearTokens } from '../lib/api-client';
import type { LoginFormData, RegisterFormData } from '@edytech/shared/validators';

// Types
export interface AuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: 'customer' | 'student' | 'admin' | 'technician';
    phone?: string;
    emailVerified: boolean;
    createdAt: string;
  };
  accessToken: string;
  refreshToken: string;
}

export interface OTPRequestResponse {
  success: boolean;
  message: string;
  expiresIn: number; // seconds
}

export interface OTPVerifyResponse {
  success: boolean;
  verified: boolean;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  newPassword: string;
}

// Auth API functions
export const authService = {
  // Login with email/password
  async login(data: LoginFormData): Promise<AuthResponse> {
    const response = await apiPost<AuthResponse>('/auth/login', data);
    setTokens(response.accessToken, response.refreshToken);
    return response;
  },

  // Register new user
  async register(data: RegisterFormData): Promise<AuthResponse> {
    const response = await apiPost<AuthResponse>('/auth/register', data);
    setTokens(response.accessToken, response.refreshToken);
    return response;
  },

  // Request OTP for phone verification
  async requestOTP(phone: string, email: string): Promise<OTPRequestResponse> {
    return apiPost<OTPRequestResponse>('/auth/otp/request', { phone, email });
  },

  // Verify OTP
  async verifyOTP(phone: string, code: string): Promise<OTPVerifyResponse> {
    return apiPost<OTPVerifyResponse>('/auth/otp/verify', { phone, code });
  },

  // Request password reset email
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return apiPost<{ message: string }>('/auth/password/reset-request', { email });
  },

  // Confirm password reset with token
  async confirmPasswordReset(data: PasswordResetConfirm): Promise<{ message: string }> {
    return apiPost<{ message: string }>('/auth/password/reset-confirm', data);
  },

  // Get current user profile
  async getProfile(): Promise<AuthResponse['user']> {
    return apiGet<AuthResponse['user']>('/auth/profile');
  },

  // Logout
  async logout(): Promise<void> {
    try {
      await apiPost('/auth/logout');
    } finally {
      clearTokens();
    }
  },

  // Social login (Firebase handled on frontend, then token exchange)
  async socialLogin(provider: 'google' | 'facebook', idToken: string): Promise<AuthResponse> {
    const response = await apiPost<AuthResponse>('/auth/social', { provider, idToken });
    setTokens(response.accessToken, response.refreshToken);
    return response;
  },
};